import { packWorkerRequest } from './worker_request.js'
import { ERROR_CODES, MESSAGE_TYPES, TIMING_CONSTANTS } from './constants.js'
import { packInterWorkerMessage, unpackInterWorkerMessageData } from './inter_worker_message.js'
import { getTimeRangeFromNow, bytesToHex } from './utils.js'
const randomUUID = () => globalThis.crypto.randomUUID()

/**
 * @typedef {import('./types.js').WorkerRequest} WorkerRequest
 * @typedef {import('./types.js').InterWorkerMessage} InterWorkerMessage
 */

/**
 * API endpoints for workers-channel service
 */
export const API_ENDPOINTS = {
  SUBMIT_MESSAGE: '/api/v1/worker/submit_inter_worker_message',
  CONSUME_MESSAGE: '/api/v1/worker/consume_inter_worker_message',
  CALL: '/api/v1/worker/call',
}

/**
 * Makes a signed POST request to the workers-channel service
 *
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Worker's Ed25519 public key
 * @param {Uint8Array} params.privKey - Worker's Ed25519 private key
 * @param {string} params.serviceUrl - Base service URL
 * @param {string} params.path - API endpoint path
 * @param {Object} params.payload - Request payload
 * @returns {Promise<*>} Response data
 * @throws {Error} If server returns non-zero code
 */
export const postWorkerRequest = async ({
  publicKey,
  privKey,
  serviceUrl,
  path,
  payload,
}) => {
  // Pack and sign the request
  const signedRequest = packWorkerRequest({
    publicKey,
    privKey,
    payload,
  })

  // Send POST request
  const { code, message, data } = await post(serviceUrl, path, signedRequest)

  // Check response code
  if (code !== ERROR_CODES.SUCCESS) {
    throw new Error(`[${code}] ${message}, path=${path}`)
  }

  return data
}

/**
 * Submits an encrypted inter-worker message to the relay server
 *
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Sender's Ed25519 public key
 * @param {Uint8Array} params.privKey - Sender's Ed25519 private key
 * @param {string} params.serviceUrl - Base service URL
 * @param {InterWorkerMessage} params.payload - Inter-worker message to send
 * @returns {Promise<void>}
 * @throws {Error} If submission fails
 */
export const submitInterWorkerMessage = ({
  publicKey,
  privKey,
  serviceUrl,
  payload,
}) => {
  return postWorkerRequest({
    publicKey,
    privKey,
    serviceUrl,
    path: API_ENDPOINTS.SUBMIT_MESSAGE,
    payload,
  })
}

/**
 * Consumes pending inter-worker messages from the relay server
 *
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Worker's Ed25519 public key
 * @param {Uint8Array} params.privKey - Worker's Ed25519 private key
 * @param {string} params.serviceUrl - Base service URL
 * @param {Object} params.payload - Request payload
 * @returns {Promise<{requests: WorkerRequest[]}>} Array of pending messages
 * @throws {Error} If consumption fails
 */
export const consumeInterWorkerMessage = ({
  publicKey,
  privKey,
  serviceUrl,
  payload,
}) => {
  return postWorkerRequest({
    publicKey,
    privKey,
    serviceUrl,
    path: API_ENDPOINTS.CONSUME_MESSAGE,
    payload,
  })
}

/**
 * Makes a POST request with JSON body
 *
 * @param {string} serviceUrl - Base service URL
 * @param {string} path - API endpoint path
 * @param {Object} data - Request body data
 * @returns {Promise<{code: number, message: string, data: *}>} Server response
 * @throws {Error} If network request fails
 */
const post = async (serviceUrl, path, data) => {
  const res = await fetch(serviceUrl + path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  return res.json()
}

/**
 * Calls a remote worker method via HTTP (synchronous RPC)
 * Uses full end-to-end encryption (same as WebSocket mode)
 *
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Caller's Ed25519 public key
 * @param {Uint8Array} params.privKey - Caller's Ed25519 private key
 * @param {string} params.serviceUrl - Base service URL
 * @param {string} params.receiver - Target worker public key (hex)
 * @param {string} params.method - Method name to call
 * @param {Array} params.params - Method parameters
 * @param {number} [params.timeout] - Timeout in milliseconds (default: 60000)
 * @returns {Promise<any>} Method result
 * @throws {Error} If call fails or times out
 */
export const callViaHTTP = async ({
  publicKey,
  privKey,
  serviceUrl,
  receiver,
  method,
  params = [],
  headers = {},
  timeout = TIMING_CONSTANTS.DEFAULT_HTTP_CALL_TIMEOUT,
}) => {
  const request_id = randomUUID()

  // 1. Build request data (before encryption)
  const requestData = {
    type: MESSAGE_TYPES.REQUEST,
    request_id,
    method,
    params,
    headers,
  }

  // 2. Encrypt request data (end-to-end encryption)
  const encryptedMessage = await packInterWorkerMessage(privKey, {
    publicKey,
    receiver_public_key: receiver,
    expire_at: getTimeRangeFromNow(timeout / 1000)[1].toISOString(),
    data: requestData,
  })

  // 3. Build outer payload (will be signed)
  const payload = {
    sender_public_key: bytesToHex(publicKey),
    receiver_public_key: receiver,
    data: encryptedMessage.data,
    salt: encryptedMessage.salt,
    expire_at: encryptedMessage.expire_at,
    timeout,
    request_id,
  }

  // 4. Pack and sign the request
  const workerRequest = packWorkerRequest({
    publicKey,
    privKey,
    payload,
  })

  // 5. Send HTTP POST request with timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout + 5000) // Add 5s buffer

  try {
    const res = await fetch(serviceUrl + API_ENDPOINTS.CALL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(workerRequest),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    const result = await res.json()

    // 6. Handle error response
    if (!result.success) {
      const errorData = result.error || {}
      const err = new Error(errorData.message || 'RPC call failed')
      err.code = errorData.code || ERROR_CODES.INTERNAL_ERROR
      err.request_id = errorData.request_id || request_id

      // Mark 503 errors as retryable (target worker offline)
      if (err.code === 503) {
        err.retryable = true
        err.message = 'Target worker is offline, please retry later'
      }

      throw err
    }

    // 7. Decrypt response data
    const responseData = await unpackInterWorkerMessageData(privKey, {
      sender_public_key: result.data.sender_public_key || receiver,
      data: result.data.data,
      salt: result.data.salt,
    })

    // 8. Verify response type and request_id
    if (responseData.type !== MESSAGE_TYPES.RESPONSE) {
      throw new Error(`Invalid response type: ${responseData.type}`)
    }

    if (responseData.request_id !== request_id) {
      throw new Error(
        `Request ID mismatch: expected ${request_id}, got ${responseData.request_id}`,
      )
    }

    // 9. Handle RPC errors in response
    if (responseData.code !== ERROR_CODES.SUCCESS) {
      const err = new Error(responseData.message || 'RPC execution error')
      err.code = responseData.code || ERROR_CODES.INTERNAL_ERROR
      throw err
    }

    // 10. Return result
    return responseData.data
  } catch (err) {
    clearTimeout(timeoutId)

    if (err.name === 'AbortError') {
      const timeoutErr = new Error(`RPC call timeout after ${timeout}ms`)
      timeoutErr.code = ERROR_CODES.TIMEOUT
      throw timeoutErr
    }

    throw err
  }
}
